import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Grid, Snackbar, Typography } from '@mui/material';
import { userApi } from '@/services/user.api';
import { configurationApi, ConfigurationConflictError } from '@/services/configuration.api';
import { LoadingState } from '@/components/common/LoadingState';
import { ErrorState } from '@/components/common/ErrorState';
import { ConfigurationCard } from '@/components/configurations/ConfigurationCard';
import { DeleteConfigurationDialog } from '@/components/configurations/DeleteConfigurationDialog';
import type { ConfigurationDetail } from '@/types';

/** Limite di configurazioni attive per appartamento (port di REACT_APP_CONFIG_LIMIT legacy). */
const CONFIG_LIMIT_PER_APARTMENT = 4;

export default function ConfigurationsPage() {
  const navigate = useNavigate();
  const [configurations, setConfigurations] = useState<ConfigurationDetail[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const loadConfigurations = (): void => {
    setIsLoading(true);
    setError(null);
    userApi
      .getMyConfigurationsDetail()
      .then((res) => {
        setConfigurations(res.configurations);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Impossibile caricare le tue configurazioni. Riprova.');
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadConfigurations();
  }, []);

  /** Configurazioni raggruppate per appartamento (per limite 4 + freeze se una è confermata). */
  const byApartment = useMemo(() => {
    const map = new Map<number, ConfigurationDetail[]>();
    for (const c of configurations ?? []) {
      const list = map.get(c.realApartmentId) ?? [];
      list.push(c);
      map.set(c.realApartmentId, list);
    }
    return map;
  }, [configurations]);

  const handleRename = (id: number, name: string): void => {
    const config = configurations?.find((c) => c.id === id);
    if (!config) return;
    configurationApi
      .rename(id, { name, realApartmentId: config.realApartmentId })
      .then(() => loadConfigurations())
      .catch(() => setToast('Impossibile rinominare la configurazione. Riprova.'));
  };

  const handleDuplicate = (id: number): void => {
    configurationApi
      .copy(id)
      .then(() => loadConfigurations())
      .catch((e: unknown) => {
        setToast(
          e instanceof ConfigurationConflictError
            ? e.message
            : 'Impossibile duplicare la configurazione. Riprova.',
        );
      });
  };

  const handleDeleteConfirm = (): void => {
    if (deleteId == null) return;
    const id = deleteId;
    setDeleteId(null);
    configurationApi
      .remove(id)
      .then(() => loadConfigurations())
      .catch(() => setToast('Impossibile eliminare la configurazione. Riprova.'));
  };

  return (
    <Box sx={{ flex: 1, px: { xs: 2, md: 6 }, py: 5 }}>
      <Typography variant="h2" gutterBottom>
        Le tue configurazioni
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Seleziona una configurazione per continuare a personalizzarla.
      </Typography>

      {isLoading && <LoadingState message="Caricamento configurazioni…" />}
      {error && <ErrorState message={error} onRetry={loadConfigurations} />}

      {!isLoading && !error && configurations && configurations.length === 0 && (
        <Typography variant="body1" color="text.secondary">
          Non risultano configurazioni associate al tuo account.
        </Typography>
      )}

      {!isLoading && !error && configurations && configurations.length > 0 && (
        <Grid container spacing={3}>
          {configurations.map((configuration, i) => {
            const siblings = byApartment.get(configuration.realApartmentId) ?? [];
            const apartmentConfirmed = siblings.some((c) => c.status === 'confirmed');
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={configuration.id}>
                <ConfigurationCard
                  configuration={configuration}
                  index={i + 1}
                  onOpen={(id) => navigate(`/configurations/${id}`)}
                  onRename={handleRename}
                  onDuplicate={handleDuplicate}
                  onDelete={(id) => setDeleteId(id)}
                  editable={!apartmentConfirmed}
                  canDuplicate={siblings.length < CONFIG_LIMIT_PER_APARTMENT}
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      <DeleteConfigurationDialog
        open={deleteId != null}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
      />

      <Snackbar
        open={toast != null}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
